/**
 * Aba Responsáveis — técnico + legal.
 *
 * Lista por tipo. Soft delete (is_active=false) mantém histórico — compliance
 * ANVISA exige rastreio de troca de RT. Toggle "incluir inativos" mostra ex-RTs.
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Briefcase,
  IdCard,
  Pencil,
  Plus,
  Stethoscope,
  Trash2,
  UserRound,
} from "lucide-react";
import toast from "react-hot-toast";

import { tenantsCompanyApi } from "@/api/tenantsCompany";
import { apiErrorMessage } from "@/api/client";
import { qk, useNotificationChannels, useTenantResponsaveis } from "@/hooks/queries";
import type {
  ConselhoTipo,
  Severity,
  TenantResponsavel,
  TenantResponsavelCreate,
  TenantResponsavelUpdate,
  TipoResponsavel,
} from "@/types";

import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Field, Input, Select } from "@/components/Field";
import { Modal } from "@/components/Modal";
import { Skeleton } from "@/components/Skeleton";
import { Tip } from "@/components/Tip";
import { SEVERIDADE_OPCOES } from "@/lib/severidade";

const CONSELHO_OPTIONS: { value: ConselhoTipo; label: string }[] = [
  { value: "CRF",   label: "CRF — Conselho Regional de Farmácia" },
  { value: "CRM",   label: "CRM — Conselho Regional de Medicina" },
  { value: "CRBM",  label: "CRBM — Biomedicina" },
  { value: "CRBio", label: "CRBio — Biólogos" },
  { value: "CREA",  label: "CREA — Engenharia" },
  { value: "CRQ",   label: "CRQ — Química" },
  { value: "CRMV",  label: "CRMV — Medicina Veterinária" },
  { value: "CRO",   label: "CRO — Odontologia" },
  { value: "CRN",   label: "CRN — Nutrição" },
  { value: "outro", label: "Outro" },
];

const UF_LIST = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
  "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
  "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

// ---------- Tab principal ----------

export function ResponsaveisTab({ tenantId }: { tenantId: string }) {
  const [includeInactive, setIncludeInactive] = useState(false);
  const list = useTenantResponsaveis(tenantId, includeInactive);
  const qc = useQueryClient();

  const [isCreating, setIsCreating] = useState<TipoResponsavel | null>(null);
  const [editTarget, setEditTarget] = useState<TenantResponsavel | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TenantResponsavel | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.tenantResponsaveis(tenantId, false) });
    void qc.invalidateQueries({ queryKey: qk.tenantResponsaveis(tenantId, true) });
    void qc.invalidateQueries({ queryKey: qk.tenantCompleteness(tenantId) });
    void qc.invalidateQueries({ queryKey: qk.tenantAuditLogs(tenantId) });
  };

  const createMut = useMutation({
    mutationFn: (p: TenantResponsavelCreate) =>
      tenantsCompanyApi.createResponsavel(tenantId, p),
    onSuccess: () => {
      toast.success("Responsável adicionado");
      setIsCreating(null);
      invalidate();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: TenantResponsavelUpdate }) =>
      tenantsCompanyApi.updateResponsavel(tenantId, id, p),
    onSuccess: () => {
      toast.success("Responsável atualizado");
      setEditTarget(null);
      invalidate();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => tenantsCompanyApi.deactivateResponsavel(tenantId, id),
    onSuccess: () => {
      toast.success("Responsável desativado");
      setDeleteTarget(null);
      invalidate();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  if (list.isLoading) return <Skeleton className="h-48 w-full" />;

  const all = list.data ?? [];
  const tecnicos = all.filter((r) => r.tipo === "tecnico");
  const legais   = all.filter((r) => r.tipo === "legal");

  return (
    <div className="space-y-4">
      {/* Toggle incluir inativos */}
      <div className="flex items-center justify-end gap-2 text-xs text-slate-500 dark:text-slate-400">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="h-4 w-4 rounded border-surface-300 dark:border-slate-700 text-brand-600"
          />
          Mostrar inativos (histórico)
        </label>
      </div>

      {/* Responsável Técnico */}
      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Stethoscope className="h-4 w-4 text-brand-600" /> Responsável Técnico
            </span>
          }
          subtitle="Obrigatório pra fins regulatórios — exige conselho profissional"
          actions={
            <Button
              size="sm"
              variant="secondary"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => setIsCreating("tecnico")}
            >
              Adicionar
            </Button>
          }
        />
        <CardBody>
          {tecnicos.length === 0 ? (
            <EmptyRow tipo="tecnico" />
          ) : (
            <ul className="space-y-2">
              {tecnicos.map((r) => (
                <ResponsavelRow
                  key={r.id}
                  r={r}
                  onEdit={() => setEditTarget(r)}
                  onDelete={() => setDeleteTarget(r)}
                />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {/* Responsável Legal */}
      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-slate-500 dark:text-slate-400" /> Responsável Legal
            </span>
          }
          subtitle="Representante legal da empresa (sócio, diretor)"
          actions={
            <Button
              size="sm"
              variant="secondary"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => setIsCreating("legal")}
            >
              Adicionar
            </Button>
          }
        />
        <CardBody>
          {legais.length === 0 ? (
            <EmptyRow tipo="legal" />
          ) : (
            <ul className="space-y-2">
              {legais.map((r) => (
                <ResponsavelRow
                  key={r.id}
                  r={r}
                  onEdit={() => setEditTarget(r)}
                  onDelete={() => setDeleteTarget(r)}
                />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {/* Modal Criar */}
      <ResponsavelFormModal
        key={isCreating ? `create-${isCreating}` : `edit-${editTarget?.id ?? "none"}`}
        open={!!isCreating || !!editTarget}
        onClose={() => {
          setIsCreating(null);
          setEditTarget(null);
        }}
        tipo={isCreating ?? editTarget?.tipo ?? "tecnico"}
        tenantId={tenantId}
        initial={editTarget}
        submitting={createMut.isPending || updateMut.isPending}
        onSubmit={(p) => {
          if (editTarget) {
            // Update — strip 'tipo' (não pode mudar)
            const { tipo: _, ...patch } = p;
            updateMut.mutate({ id: editTarget.id, p: patch });
          } else {
            createMut.mutate(p);
          }
        }}
      />

      {/* Confirm desativar */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Desativar responsável?"
        message={
          deleteTarget
            ? `${deleteTarget.nome} será marcado como inativo. O histórico fica preservado pra compliance — você pode reativar depois.`
            : ""
        }
        confirmLabel="Desativar"
        loading={deleteMut.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
      />
    </div>
  );
}

// ---------- Subcomponentes ----------

function EmptyRow({ tipo }: { tipo: TipoResponsavel }) {
  return (
    <p className="py-4 text-center text-sm text-slate-500 dark:text-slate-400">
      Nenhum responsável {tipo === "tecnico" ? "técnico" : "legal"} cadastrado.
    </p>
  );
}

function ResponsavelRow({
  r,
  onEdit,
  onDelete,
}: {
  r: TenantResponsavel;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <li className="flex items-start justify-between rounded-lg border border-surface-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <UserRound className="h-4 w-4 flex-none text-slate-400 dark:text-slate-500" />
          <span className="font-medium text-slate-900 dark:text-slate-100">{r.nome}</span>
          {!r.is_active && (
            <Badge tone="default">Inativo</Badge>
          )}
        </div>
        <div className="mt-1 grid gap-x-4 gap-y-0.5 text-xs text-slate-500 dark:text-slate-400 sm:grid-cols-2">
          {r.cargo && <span>{r.cargo}</span>}
          {r.cpf && (
            <span className="inline-flex items-center gap-1">
              <IdCard className="h-3 w-3" /> CPF: {formatCpfDisplay(r.cpf)}
            </span>
          )}
          {r.conselho_tipo && r.conselho_numero && (
            <span className="font-medium text-brand-700">
              {r.conselho_tipo} {r.conselho_numero}/{r.conselho_uf || "?"}
            </span>
          )}
          {r.email && <span>{r.email}</span>}
          {r.telefone && <span>{r.telefone}</span>}
        </div>
      </div>

      <div className="ml-3 flex items-center gap-1">
        {r.is_active && (
          <>
            <Button
              size="sm"
              variant="ghost"
              icon={<Pencil className="h-4 w-4" />}
              onClick={onEdit}
            />
            <Button
              size="sm"
              variant="ghost"
              icon={<Trash2 className="h-4 w-4 text-red-500" />}
              onClick={onDelete}
            />
          </>
        )}
      </div>
    </li>
  );
}

// ---------- Form modal ----------

function ResponsavelFormModal({
  open,
  onClose,
  tipo,
  tenantId,
  initial,
  submitting,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  tipo: TipoResponsavel;
  tenantId: string;
  initial?: TenantResponsavel | null;
  submitting?: boolean;
  onSubmit: (p: TenantResponsavelCreate) => void;
}) {
  const [form, setForm] = useState<TenantResponsavelCreate>(() => makeInitial(tipo, initial));
  // Canais pra dropdown de auto-subscribe.
  const channels = useNotificationChannels(tenantId);

  useEffect(() => {
    setForm(makeInitial(tipo, initial));
  }, [tipo, initial]);

  function set<K extends keyof TenantResponsavelCreate>(
    k: K,
    v: TenantResponsavelCreate[K] | string,
  ) {
    setForm((f) => ({ ...f, [k]: (v === "" ? null : v) as TenantResponsavelCreate[K] }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit(form);
  }

  const isTecnico = tipo === "tecnico";
  const titleLabel = isTecnico ? "Responsável Técnico" : "Responsável Legal";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? `Editar ${titleLabel}` : `Novo ${titleLabel}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" form="resp-form" loading={submitting}>
            {initial ? "Salvar" : "Adicionar"}
          </Button>
        </>
      }
    >
      <form id="resp-form" onSubmit={handleSubmit} className="space-y-4">
        <Field label="Nome completo *">
          <Input
            value={form.nome}
            onChange={(e) => set("nome", e.target.value)}
            required
            minLength={2}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="CPF">
            <Input
              value={formatCpfDisplay(form.cpf || "")}
              onChange={(e) => set("cpf", e.target.value.replace(/\D/g, ""))}
              placeholder="000.000.000-00"
              maxLength={14}
            />
          </Field>
          <Field label="Cargo">
            <Input
              value={form.cargo || ""}
              onChange={(e) => set("cargo", e.target.value)}
              placeholder={isTecnico ? "Farmacêutico, Biomédico..." : "Diretor, Sócio..."}
            />
          </Field>
        </div>

        {isTecnico && (
          <div className="rounded-lg border border-brand-100 bg-brand-50/50 p-3">
            <p className="mb-3 text-xs font-medium text-brand-800">
              Conselho profissional (obrigatório pra RT)
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Conselho *">
                <Select
                  value={form.conselho_tipo || ""}
                  onChange={(e) =>
                    set("conselho_tipo", (e.target.value || null) as ConselhoTipo | null)
                  }
                  options={[{ value: "", label: "Selecione..." }, ...CONSELHO_OPTIONS]}
                  required
                />
              </Field>
              <Field label="Número *">
                <Input
                  value={form.conselho_numero || ""}
                  onChange={(e) => set("conselho_numero", e.target.value)}
                  required
                />
              </Field>
              <Field label="UF *">
                <Select
                  value={form.conselho_uf || ""}
                  onChange={(e) => set("conselho_uf", e.target.value || null)}
                  options={[
                    { value: "", label: "—" },
                    ...UF_LIST.map((u) => ({ value: u, label: u })),
                  ]}
                  required
                />
              </Field>
            </div>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="E-mail">
            <Input
              type="email"
              value={form.email || ""}
              onChange={(e) => set("email", e.target.value)}
            />
          </Field>
          <Field label="Telefone">
            <Input
              type="tel"
              value={form.telefone || ""}
              onChange={(e) => set("telefone", e.target.value)}
              placeholder="+55 11 99999-9999"
            />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={
              <>
                Vigência início
                <Tip label="Vigência início">
                  Opcional — a data em que esta pessoa assumiu o papel.
                </Tip>
              </>
            }
          >
            <Input
              type="date"
              value={form.vigencia_inicio || ""}
              onChange={(e) => set("vigencia_inicio", e.target.value || null)}
            />
          </Field>
          <Field
            label={
              <>
                Vigência fim
                <Tip label="Vigência fim">
                  Opcional — só pra vínculo com prazo definido.
                </Tip>
              </>
            }
          >
            <Input
              type="date"
              value={form.vigencia_fim || ""}
              onChange={(e) => set("vigencia_fim", e.target.value || null)}
            />
          </Field>
        </div>

        {/* Auto-subscribe pra alertas */}
        <div className="rounded-lg border border-surface-200 dark:border-slate-700 bg-surface-50/50 dark:bg-slate-800/40 p-3 space-y-3">
          <p className="text-xs font-medium text-slate-700 dark:text-slate-200">
            Notificações de alerta (opcional)
          </p>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 -mt-2">
            Vincula este responsável a um canal de notificação. Sempre que um alerta do tenant
            disparar com severidade ≥ selecionada, o canal recebe — sem precisar criar subscription
            manual.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Canal de notificação">
              <Select
                value={form.notify_channel_id || ""}
                onChange={(e) => set("notify_channel_id", e.target.value || null)}
                options={[
                  { value: "", label: "— não notificar —" },
                  ...(channels.data ?? []).map((c) => ({
                    value: c.id,
                    label: `${c.name} (${c.kind})`,
                  })),
                ]}
              />
            </Field>
            <Field
            label={
              <>
                Severidade mínima
                <Tip label="Severidade mínima">
                  Filtra o que este responsável recebe: <strong>info</strong> entrega tudo,
                  <strong> warning</strong> entrega warning e critical,
                  <strong> critical</strong> só o mais grave.
                </Tip>
              </>
            }
          >
              <Select
                value={form.notify_min_severity || ""}
                onChange={(e) =>
                  set("notify_min_severity", (e.target.value || null) as Severity | null)
                }
                disabled={!form.notify_channel_id}
                options={[{ value: "", label: "—" }, ...SEVERIDADE_OPCOES]}
              />
            </Field>
          </div>
        </div>
      </form>
    </Modal>
  );
}

// ---------- Helpers ----------

function makeInitial(
  tipo: TipoResponsavel,
  source: TenantResponsavel | null | undefined,
): TenantResponsavelCreate {
  if (source) {
    return {
      tipo: source.tipo,
      nome: source.nome,
      cpf: source.cpf,
      cargo: source.cargo,
      email: source.email,
      telefone: source.telefone,
      conselho_tipo: source.conselho_tipo,
      conselho_numero: source.conselho_numero,
      conselho_uf: source.conselho_uf,
      vigencia_inicio: source.vigencia_inicio,
      vigencia_fim: source.vigencia_fim,
      notify_channel_id: source.notify_channel_id,
      notify_min_severity: source.notify_min_severity,
    };
  }
  return {
    tipo,
    nome: "",
    cpf: null,
    cargo: null,
    email: null,
    telefone: null,
    conselho_tipo: null,
    conselho_numero: null,
    conselho_uf: null,
    vigencia_inicio: null,
    vigencia_fim: null,
    notify_channel_id: null,
    notify_min_severity: null,
  };
}

function formatCpfDisplay(raw: string | null | undefined): string {
  const d = (raw || "").replace(/\D/g, "");
  if (d.length !== 11) return raw || "";
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}
